/** The four dates a pilot runs on, as `YYYY-MM-DD`, plus which week a day is in. */
export interface PilotTimeline {
  /** Week 1's Monday: the first Monday on or after the cohort's start date. */
  firstMailOn: string;
  /** Week N's Monday, the last practice mail. */
  lastMailOn: string;
  /** Week N+1's Monday, when the closing mail goes out. */
  closingMailOn: string;
  /** Week N+2's Monday, when members lose access. */
  accessEndsOn: string;
  /** The practice week `date` falls in (1..N), or null outside those weeks. */
  weekOn: (date: string) => number | null;
}

const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * Dates are compared and shifted in UTC so that a whole day is always exactly
 * 86 400 000 ms — a local-time Date would lose or gain an hour across DST and
 * land a Monday on the Sunday before it.
 */
function toUtcDate(date: string): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MILLISECONDS_PER_DAY);
}

function firstMondayOnOrAfter(date: Date): Date {
  return addDays(date, (8 - date.getUTCDay()) % 7);
}

/**
 * A pilot's calendar, derived from the day it starts and how many weeks it
 * runs. Every mail lands on a Monday, so the whole timeline hangs off the first
 * Monday on or after the start date.
 */
export function pilotTimeline(startAt: string, weeks: number): PilotTimeline {
  const weekOneMonday = firstMondayOnOrAfter(toUtcDate(startAt));

  return {
    firstMailOn: toDateString(weekOneMonday),
    lastMailOn: toDateString(addDays(weekOneMonday, (weeks - 1) * 7)),
    closingMailOn: toDateString(addDays(weekOneMonday, weeks * 7)),
    accessEndsOn: toDateString(addDays(weekOneMonday, (weeks + 1) * 7)),
    weekOn: (date: string) => {
      const days =
        (toUtcDate(date).getTime() - weekOneMonday.getTime()) / MILLISECONDS_PER_DAY;
      if (days < 0) return null;

      const week = Math.floor(days / 7) + 1;
      return week > weeks ? null : week;
    },
  };
}
